import { Component } from '@theme/component';
import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';

/**
 * Header search trigger.
 * - Search bar mode: desktop shows the bar; mobile shows an icon that opens the modal.
 * - Icon mode: search icon opens/closes the predictive search modal on all viewports.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement} [searchToggle] - The search toggle button (icon / mobile).
 *
 * @extends {Component<Refs>}
 */
class SearchButton extends Component {
  /** @type {DialogComponent | null} */
  #dialog = null;

  /** @type {AbortController | null} */
  #controller = null;

  connectedCallback() {
    super.connectedCallback();

    // Modal wiring is needed whenever a toggle button exists
    // (icon-only mode, or search-bar mode on mobile).
    if (!this.refs.searchToggle) return;

    this.#controller = new AbortController();
    const { signal } = this.#controller;

    customElements.whenDefined('dialog-component').then(() => {
      if (signal.aborted) return;

      const dialog = document.getElementById('search-modal');
      if (!(dialog instanceof DialogComponent)) return;

      this.#dialog = dialog;
      dialog.addEventListener(DialogOpenEvent.eventName, this.#syncExpanded, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#syncExpanded, { signal });
      this.#syncExpanded();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
    this.#controller = null;
    this.#dialog = null;
  }

  /**
   * Toggles the search modal.
   * @param {Event} [event]
   */
  toggle = (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();

    this.#getDialog()?.toggleDialog();
  };

  /**
   * @returns {DialogComponent | null}
   */
  #getDialog() {
    if (this.#dialog instanceof DialogComponent) return this.#dialog;

    const dialog = document.getElementById('search-modal');
    if (dialog instanceof DialogComponent) {
      this.#dialog = dialog;
      return dialog;
    }

    return null;
  }

  #syncExpanded = () => {
    const { searchToggle } = this.refs;
    if (!searchToggle) return;

    const open = Boolean(this.#getDialog()?.refs.dialog?.open);
    searchToggle.setAttribute('aria-expanded', String(open));
    this.classList.toggle('search-action--open', open);
  };
}

if (!customElements.get('search-button')) {
  customElements.define('search-button', SearchButton);
}
